import { useTranslation } from 'react-i18next';
import { ArrowDownUp, CalendarClock, ChevronDown, ListOrdered, RotateCcw, Search, Triangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import DateField from '../DateField';
import {
  DEFAULT_BOARD_DASHBOARD_FILTERS,
  type BoardDashboardFilters,
  type BoardDashboardSort,
} from '../../../types/board-dashboard';
import type { GradingSystemRef } from '../../../types/device';

interface BoardDashboardFilterBarProps {
  filters: BoardDashboardFilters;
  onChange: (filters: BoardDashboardFilters) => void;
  /** The board's own grade ladder — the only place the order of a scale lives. */
  grades: NonNullable<GradingSystemRef['grades']>;
  /** Every angle the board holds a problem at; empty on a fixed-angle wall. */
  inclinations: number[];
  /** Typed name, kept separate so the request can be debounced. */
  nameInput: string;
  onNameInputChange: (value: string) => void;
}

/** Same one-line bar the route-setting board uses: small buttons, own popover. */
const TRIGGER = 'h-8 max-w-[12rem] justify-between gap-1.5 px-2.5 text-xs font-normal';
const ACTIVE = 'border-primary/60 bg-primary/5';

const SORTS: BoardDashboardSort[] = [
  'created_at',
  'name',
  'grade',
  'sends',
  'attempts',
  'rating',
  'lightings',
  'shares',
];

export function BoardDashboardFilterBar({
  filters,
  onChange,
  grades,
  inclinations,
  nameInput,
  onNameInputChange,
}: BoardDashboardFilterBarProps) {
  const { t } = useTranslation();

  const set = (patch: Partial<BoardDashboardFilters>) => onChange({ ...filters, ...patch });

  const gradeName = (id: string | null) => grades.find((grade) => grade.id === id)?.name ?? null;
  const gradeNarrowed = filters.gradeMin !== null || filters.gradeMax !== null;
  const datesNarrowed = filters.createdFrom !== null || filters.createdTo !== null;

  const filtering =
    nameInput !== '' ||
    gradeNarrowed ||
    datesNarrowed ||
    filters.inclination !== null ||
    filters.circuit !== 'all' ||
    filters.sort !== DEFAULT_BOARD_DASHBOARD_FILTERS.sort ||
    filters.direction !== DEFAULT_BOARD_DASHBOARD_FILTERS.direction;

  const gradeLabel = gradeNarrowed
    ? `${gradeName(filters.gradeMin) ?? '…'} – ${gradeName(filters.gradeMax) ?? '…'}`
    : t('gymAdmin.boardDashboard.filters.allGrades');

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="board-dashboard-filters">
      <div className="relative min-w-[10rem] flex-1">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={nameInput}
          onChange={(event) => onNameInputChange(event.target.value)}
          placeholder={t('gymAdmin.boardDashboard.filters.namePlaceholder')}
          aria-label={t('gymAdmin.boardDashboard.filters.name')}
          className="h-8 pl-8 text-xs"
        />
      </div>

      {/* Grade range, as the two ends of the board's own ladder. */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(TRIGGER, gradeNarrowed && ACTIVE)}
            disabled={grades.length === 0}
          >
            <span className="truncate">{gradeLabel}</span>
            <ChevronDown className="size-3.5 shrink-0 opacity-50" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 space-y-3 p-3" align="start">
          {(['gradeMin', 'gradeMax'] as const).map((bound) => (
            <div key={bound} className="space-y-1.5">
              <Label className="text-xs">{t(`gymAdmin.boardDashboard.filters.${bound}`)}</Label>
              <ScrollArea className="h-32 rounded-md border">
                <div className="p-1">
                  <button
                    type="button"
                    onClick={() => set({ [bound]: null } as Partial<BoardDashboardFilters>)}
                    className={cn(
                      'w-full rounded px-2 py-1 text-left text-sm hover:bg-accent',
                      filters[bound] === null && 'bg-accent font-medium'
                    )}
                  >
                    {t('gymAdmin.boardDashboard.filters.anyGrade')}
                  </button>
                  {grades.map((grade) => (
                    <button
                      key={grade.id}
                      type="button"
                      onClick={() => set({ [bound]: grade.id } as Partial<BoardDashboardFilters>)}
                      className={cn(
                        'w-full rounded px-2 py-1 text-left text-sm hover:bg-accent',
                        filters[bound] === grade.id && 'bg-accent font-medium'
                      )}
                    >
                      {grade.name}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          ))}
        </PopoverContent>
      </Popover>

      {/* Only a board that can be tilted has angles to choose between. */}
      {inclinations.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(TRIGGER, filters.inclination !== null && ACTIVE)}
            >
              <Triangle className="size-3.5 shrink-0 opacity-70" aria-hidden />
              <span className="truncate">
                {filters.inclination === null
                  ? t('gymAdmin.boardDashboard.filters.allAngles')
                  : t('gymAdmin.boardDashboard.degrees', { value: filters.inclination })}
              </span>
              <ChevronDown className="size-3.5 shrink-0 opacity-50" aria-hidden />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-40 p-1" align="start">
            <button
              type="button"
              onClick={() => set({ inclination: null })}
              className={cn(
                'w-full rounded px-2 py-1 text-left text-sm hover:bg-accent',
                filters.inclination === null && 'bg-accent font-medium'
              )}
            >
              {t('gymAdmin.boardDashboard.filters.allAngles')}
            </button>
            {inclinations.map((angle) => (
              <button
                key={angle}
                type="button"
                onClick={() => set({ inclination: angle })}
                className={cn(
                  'w-full rounded px-2 py-1 text-left text-sm hover:bg-accent',
                  filters.inclination === angle && 'bg-accent font-medium'
                )}
              >
                {t('gymAdmin.boardDashboard.degrees', { value: angle })}
              </button>
            ))}
          </PopoverContent>
        </Popover>
      )}

      {/* Circuits are a different thing to set than problems, so they filter
          three ways rather than as a checkbox. */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(TRIGGER, filters.circuit !== 'all' && ACTIVE)}
          >
            <ListOrdered className="size-3.5 shrink-0 opacity-70" aria-hidden />
            <span className="truncate">{t(`gymAdmin.boardDashboard.filters.circuit.${filters.circuit}`)}</span>
            <ChevronDown className="size-3.5 shrink-0 opacity-50" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-40 p-1" align="start">
          {(['all', 'yes', 'no'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => set({ circuit: value })}
              className={cn(
                'w-full rounded px-2 py-1 text-left text-sm hover:bg-accent',
                filters.circuit === value && 'bg-accent font-medium'
              )}
            >
              {t(`gymAdmin.boardDashboard.filters.circuit.${value}`)}
            </button>
          ))}
        </PopoverContent>
      </Popover>

      {/* When it was set: two whole days, ends included. */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className={cn(TRIGGER, datesNarrowed && ACTIVE)}>
            <CalendarClock className="size-3.5 shrink-0 opacity-70" aria-hidden />
            <span className="truncate">
              {datesNarrowed
                ? `${filters.createdFrom ?? '…'} → ${filters.createdTo ?? '…'}`
                : t('gymAdmin.boardDashboard.filters.anyDate')}
            </span>
            <ChevronDown className="size-3.5 shrink-0 opacity-50" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 space-y-3 p-3" align="start">
          <DateField
            id="board-dashboard-created-from"
            label={t('gymAdmin.boardDashboard.filters.createdFrom')}
            value={filters.createdFrom ?? ''}
            onChange={(value) => set({ createdFrom: value || null })}
          />
          <DateField
            id="board-dashboard-created-to"
            label={t('gymAdmin.boardDashboard.filters.createdTo')}
            value={filters.createdTo ?? ''}
            onChange={(value) => set({ createdTo: value || null })}
          />
        </PopoverContent>
      </Popover>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className={TRIGGER}>
            <ArrowDownUp className="size-3.5 shrink-0 opacity-70" aria-hidden />
            <span className="truncate">{t(`gymAdmin.boardDashboard.sorts.${filters.sort}`)}</span>
            <ChevronDown className="size-3.5 shrink-0 opacity-50" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-1" align="start">
          {SORTS.map((sort) => (
            <button
              key={sort}
              type="button"
              // Picking the sort you are already on flips it, which is the one
              // thing a separate direction control would be needed for.
              onClick={() =>
                set(
                  filters.sort === sort
                    ? { direction: filters.direction === 'asc' ? 'desc' : 'asc' }
                    : { sort, direction: sort === 'name' ? 'asc' : 'desc' }
                )
              }
              className={cn(
                'flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm hover:bg-accent',
                filters.sort === sort && 'bg-accent font-medium'
              )}
            >
              {t(`gymAdmin.boardDashboard.sorts.${sort}`)}
              {filters.sort === sort && (
                <span className="text-xs text-muted-foreground">
                  {t(`gymAdmin.boardDashboard.directions.${filters.direction}`)}
                </span>
              )}
            </button>
          ))}
        </PopoverContent>
      </Popover>

      {filtering && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-xs"
          onClick={() => {
            onNameInputChange('');
            onChange(DEFAULT_BOARD_DASHBOARD_FILTERS);
          }}
        >
          <RotateCcw className="mr-1.5 size-3.5" aria-hidden />
          {t('gymAdmin.boardDashboard.filters.reset')}
        </Button>
      )}
    </div>
  );
}
